import React, { useState } from "react";
import { Layout, Button, Spin, Typography } from "antd";
import {
  SettingOutlined,
  SwapOutlined,
  SafetyCertificateOutlined,
  RetweetOutlined,
  ApartmentOutlined,
  DesktopOutlined,
  ClockCircleOutlined,
  AuditOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  ExperimentOutlined,
  DeploymentUnitOutlined,
  BarChartOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  RocketOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import "../styles/signin.css";
import { THEME } from "../styles/colors";
import logoNavy from "../assets/transparent-logo-navy.png";
import AuthModal from "../components/auth/AuthModal";
import {
  HeroPipeline,
  EffortVsCapability,
  MiniWorkflow,
} from "../components/marketing/LandingVisuals";

const { Text } = Typography;
const { Content, Footer } = Layout;

const HERO_STATS = [
  "8 capability areas",
  "Reusable automation assets",
  "Evidence & traceability",
  "Built for financial services",
];

const CAPABILITIES = [
  {
    icon: <SwapOutlined />,
    title: "Reconciliation Automation",
    desc: "Automated comparison and reconciliation across databases, files, reports and operational outputs — validating outcomes with greater speed and consistency.",
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: "Validation Automation",
    desc: "Automate business rules, calculations, controls and expected outcomes — improving delivery confidence and cutting manual verification.",
  },
  {
    icon: <RetweetOutlined />,
    title: "Regression Automation",
    desc: "Reusable regression assets supporting system, integration, user acceptance and release validation activities.",
  },
  {
    icon: <ApartmentOutlined />,
    title: "Environment & Workflow Orchestration",
    desc: "Automate readiness checks, workflow execution, operational activities and release preparation processes.",
  },
  {
    icon: <DesktopOutlined />,
    title: "UI Automation",
    desc: "Automate operational and user-facing processes across enterprise platforms including PControl, Charles River and Aladdin.",
  },
  {
    icon: <ClockCircleOutlined />,
    title: "SOD & EOD Processing",
    desc: "Execute, validate and monitor start-of-day and end-of-day operational activities, controls and outputs.",
  },
  {
    icon: <AuditOutlined />,
    title: "Evidence Generation & Traceability",
    desc: "Automated evidence capture supporting governance, audit, release decisions and operational sign-off.",
  },
  {
    icon: <DatabaseOutlined />,
    title: "Migration Validation",
    desc: "Validate and reconcile migrated data, interfaces, reports and business outcomes across transformation and onboarding programs.",
  },
];

const MODULES = [
  {
    icon: <FileTextOutlined />,
    title: "Document Library",
    desc: "Functional specs, SQL and control documents in one place — versioned and reusable.",
  },
  {
    icon: <ExperimentOutlined />,
    title: "Test Suites & Tasks",
    desc: "Build suites and reconciliation/process tasks; organise a reusable test catalogue.",
  },
  {
    icon: <DeploymentUnitOutlined />,
    title: "Master Suite Workflow",
    desc: "Orchestrate multi-step runs across environments as a visual workflow.",
  },
  {
    icon: <SwapOutlined />,
    title: "Reconciliation Engine",
    desc: "File-to-Spec, File-to-DB, DB-to-DB and File-to-File reconciliation with field mapping.",
  },
  {
    icon: <DesktopOutlined />,
    title: "App / UI Automation",
    desc: "Drive desktop and web platforms — Aladdin, Charles River, PControl and more.",
  },
  {
    icon: <BarChartOutlined />,
    title: "Results & Evidence",
    desc: "Drill into outcomes, validate data and generate audit-ready evidence.",
  },
  {
    icon: <ApiOutlined />,
    title: "ARC Gateway",
    desc: "Generate data interface files (CRIMS, Aladdin, HiPortfolio) directly from a spec.",
  },
  {
    icon: <SettingOutlined />,
    title: "Environments & Agents",
    desc: "Manage environments, database connections and remote execution agents.",
  },
];

const BENEFITS = [
  {
    icon: <ThunderboltOutlined />,
    text: "Reduced delivery effort by automating repetitive activities.",
  },
  {
    icon: <CheckCircleOutlined />,
    text: "Improved quality through repeatable, consistent validation.",
  },
  {
    icon: <SafetyOutlined />,
    text: "Reduced operational risk through systematic verification of controls and outcomes.",
  },
  {
    icon: <RocketOutlined />,
    text: "Accelerated releases through streamlined validation and readiness.",
  },
  {
    icon: <AuditOutlined />,
    text: "Improved auditability through automated evidence and traceability.",
  },
  {
    icon: <ReloadOutlined />,
    text: "Reusable capability that strengthens with every program and release.",
  },
];

function Band({ id, variant = "white", children }) {
  return (
    <section id={id} className={`arc-section arc-section--${variant}`}>
      <div className="arc-container">{children}</div>
    </section>
  );
}

function Signin(props) {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");

  const openAuth = (tab) => {
    setAuthTab(tab);
    setAuthModalOpen(true);
  };

  const closeAuth = () => {
    if (!props.loading) setAuthModalOpen(false);
  };

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <Layout className="layout-default layout-signin layout-marketing">
      <header className="marketing-header signin-fixed-header">
        <div className="header-col header-brand marketing-brand">
          <img src={logoNavy} className="signin-header-logo" alt="ARC" />
        </div>
        <div className="marketing-header-actions">
          <Button
            type="text"
            className="marketing-btn-ghost"
            onClick={() => openAuth("login")}
          >
            Log in
          </Button>
          <Button
            type="primary"
            className="marketing-btn-primary"
            onClick={() => openAuth("register")}
          >
            Register
          </Button>
        </div>
      </header>

      <Content className="signin marketing-content">
        {props.loading ? (
          <div className="marketing-loading">
            <Spin
              indicator={
                <SettingOutlined
                  style={{ fontSize: 48, color: THEME.primary }}
                  spin
                />
              }
            />
            <Text type="secondary">Signing you in…</Text>
          </div>
        ) : (
          <>
            {/* ── Hero ───────────────────────────────────────── */}
            <section className="arc-section arc-section--hero">
              <div className="arc-container arc-hero">
                <div className="arc-hero-copy">
                  <span className="arc-eyebrow">
                    AUTOMATED RECONCILIATION CENTRE · AUTOMATION-ENHANCED
                    TRANSFORMATION DELIVERY
                  </span>
                  <h1 className="arc-hero-title">
                    Turn repeatable delivery effort into reusable capability
                  </h1>
                  <p className="arc-hero-lead">
                    ARC automates reconciliation, validation, regression,
                    operational processes and release activities across complex
                    financial services environments — reducing delivery effort
                    while building capability that compounds across every
                    program and release.
                  </p>
                  <div className="arc-hero-actions">
                    <Button
                      type="primary"
                      size="large"
                      className="marketing-btn-primary"
                      onClick={() => openAuth("login")}
                    >
                      Get started
                    </Button>
                    <Button
                      size="large"
                      className="marketing-btn-outline"
                      onClick={() => scrollTo("arc-capabilities")}
                    >
                      Explore capabilities
                    </Button>
                  </div>
                  <ul className="arc-hero-stats">
                    {HERO_STATS.map((s) => (
                      <li key={s}>
                        <CheckCircleOutlined /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="arc-hero-visual">
                  <HeroPipeline />
                </div>
              </div>
            </section>

            {/* ── Positioning band ───────────────────────────── */}
            <section className="arc-section arc-section--navy arc-positioning">
              <div className="arc-container">
                <h2 className="arc-positioning-title">
                  ARC is not a testing tool. It is a transformation capability
                  platform.
                </h2>
                <p className="arc-positioning-sub">
                  Traditional delivery repeatedly consumes effort.{" "}
                  <strong>ARC repeatedly creates capability</strong> — for
                  migration, operational readiness, release validation and
                  ongoing change.
                </p>
              </div>
            </section>

            {/* ── Challenge → Approach ───────────────────────── */}
            <Band variant="white">
              <div className="arc-split">
                <div className="arc-split-copy">
                  <span className="arc-kicker">THE CHALLENGE</span>
                  <h2 className="arc-section-title">
                    The same effort, repeated every program
                  </h2>
                  <p className="arc-body">
                    Across testing, validation, reconciliation and operational
                    readiness, organisations repeat the same effort project
                    after project. Test cases are recreated, reconciliations
                    rebuilt and sign-offs performed manually — and the knowledge
                    is often lost before the next initiative begins.
                  </p>
                  <span className="arc-kicker arc-kicker--accent">
                    THE ARC APPROACH
                  </span>
                  <p className="arc-body">
                    ARC captures and automates those repeatable activities,
                    turning them into a growing library of reusable assets — so
                    delivery becomes more efficient over time instead of
                    resetting with every release.
                  </p>
                </div>
                <div className="arc-split-visual">
                  <EffortVsCapability />
                </div>
              </div>
            </Band>

            {/* ── Capabilities ───────────────────────────────── */}
            <Band id="arc-capabilities" variant="surface">
              <div className="arc-section-head">
                <span className="arc-kicker arc-kicker--accent">
                  CAPABILITY AREAS
                </span>
                <h2 className="arc-section-title">
                  Automation-enhanced delivery across eight core areas
                </h2>
              </div>
              <div className="arc-grid arc-grid--4">
                {CAPABILITIES.map((c) => (
                  <div className="arc-card" key={c.title}>
                    <div className="arc-card-icon">{c.icon}</div>
                    <h3 className="arc-card-title">{c.title}</h3>
                    <p className="arc-card-desc">{c.desc}</p>
                  </div>
                ))}
              </div>
            </Band>

            {/* ── Inside ARC (modules) ───────────────────────── */}
            <Band variant="white">
              <div className="arc-section-head">
                <span className="arc-kicker">ONE WORKSPACE</span>
                <h2 className="arc-section-title">Everything inside ARC</h2>
                <div className="arc-miniflow">
                  <MiniWorkflow />
                </div>
              </div>
              <div className="arc-grid arc-grid--4">
                {MODULES.map((m) => (
                  <div className="arc-card arc-card--soft" key={m.title}>
                    <div className="arc-card-icon arc-card-icon--soft">
                      {m.icon}
                    </div>
                    <h3 className="arc-card-title">{m.title}</h3>
                    <p className="arc-card-desc">{m.desc}</p>
                  </div>
                ))}
              </div>
            </Band>

            {/* ── Why ARC / benefits ─────────────────────────── */}
            <Band variant="surface">
              <div className="arc-section-head">
                <span className="arc-kicker arc-kicker--accent">WHY ARC</span>
                <h2 className="arc-section-title">
                  A delivery model that gets stronger over time
                </h2>
              </div>
              <div className="arc-grid arc-grid--3">
                {BENEFITS.map((b) => (
                  <div className="arc-benefit" key={b.text}>
                    <span className="arc-benefit-icon">{b.icon}</span>
                    <span className="arc-benefit-text">{b.text}</span>
                  </div>
                ))}
              </div>
            </Band>

            {/* ── Closing CTA ────────────────────────────────── */}
            <section className="arc-section arc-section--navy arc-cta">
              <div className="arc-container">
                <h2 className="arc-cta-title">
                  Every transformation does more than deliver change — it
                  strengthens your ability to deliver the next one.
                </h2>
                <div className="arc-hero-actions arc-cta-actions">
                  <Button
                    type="primary"
                    size="large"
                    className="marketing-btn-on-navy"
                    onClick={() => openAuth("login")}
                  >
                    Get started <ArrowRightOutlined />
                  </Button>
                  <Button
                    size="large"
                    className="marketing-btn-ghost-navy"
                    onClick={() => openAuth("register")}
                  >
                    Register
                  </Button>
                </div>
              </div>
            </section>
          </>
        )}
      </Content>

      <Footer className="marketing-footer">
        <Text type="secondary">
          © {new Date().getFullYear()} ARC — Automated Reconciliation Centre
        </Text>
      </Footer>

      <AuthModal
        open={authModalOpen}
        onClose={closeAuth}
        activeTab={authTab}
        onTabChange={setAuthTab}
        onLogin={props.onLogin}
        loginInvalid={props.loginInvalid}
        loading={props.loading}
      />
    </Layout>
  );
}

export default Signin;
